import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { Button } from './ui/button';

// Tooltip Tour Component
export function TooltipTour({ steps, storageKey, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    // Check if user has seen this tour
    const hasSeenTour = localStorage.getItem(storageKey);
    if (!hasSeenTour && steps.length > 0) {
      setIsVisible(true);
      positionTooltip();
    }
  }, [storageKey, steps]);

  useEffect(() => {
    if (isVisible) {
      positionTooltip();
    }
  }, [currentStep, isVisible]);

  const positionTooltip = () => {
    const step = steps[currentStep];
    if (!step?.target) return;

    const element = document.querySelector(step.target);
    if (element) {
      const rect = element.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

      // Add highlight to element
      element.style.position = 'relative';
      element.style.zIndex = '1001';
      element.style.boxShadow = '0 0 0 4px rgba(249, 115, 22, 0.5)';
      element.style.borderRadius = '8px';

      // Position tooltip
      let top = rect.bottom + scrollTop + 12;
      let left = rect.left + scrollLeft;

      // Adjust if tooltip would go off screen
      if (left + 320 > window.innerWidth) {
        left = window.innerWidth - 340;
      }
      if (top + 200 > window.innerHeight + scrollTop) {
        top = rect.top + scrollTop - 200;
      }

      setPosition({ top, left });
    }
  };

  const clearHighlights = () => {
    steps.forEach(step => {
      const element = document.querySelector(step.target);
      if (element) {
        element.style.position = '';
        element.style.zIndex = '';
        element.style.boxShadow = '';
        element.style.borderRadius = '';
      }
    });
  };

  const handleNext = () => {
    clearHighlights();
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    clearHighlights();
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    clearHighlights();
    localStorage.setItem(storageKey, 'true');
    setIsVisible(false);
    onComplete?.();
  };

  const handleSkip = () => {
    clearHighlights();
    localStorage.setItem(storageKey, 'true');
    setIsVisible(false);
  };

  if (!isVisible || steps.length === 0) return null;

  const step = steps[currentStep];

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-[1000]"
        onClick={handleSkip}
      />
      
      {/* Tooltip */}
      <div
        className="fixed z-[1002] w-80 bg-white rounded-xl shadow-2xl border border-slate-200 animate-fade-in"
        style={{ top: position.top, left: position.left }}
        data-testid="tooltip-tour"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
              {currentStep + 1}
            </span>
            <span className="text-sm text-slate-500">of {steps.length}</span>
          </div>
          <button onClick={handleSkip} className="text-slate-400 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="font-bold text-slate-900 mb-2">{step.title}</h3>
          <p className="text-sm text-slate-600">{step.description}</p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-100 bg-slate-50 rounded-b-xl">
          <button 
            onClick={handleSkip}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            Skip tour
          </button>
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button variant="outline" size="sm" onClick={handlePrev}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
            <Button size="sm" onClick={handleNext} className="bg-orange-500 hover:bg-orange-600">
              {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
              {currentStep < steps.length - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

// Video Tutorial Modal Component
export function VideoTutorialModal({ isOpen, onClose, title, videoId, description, steps }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden animate-fade-in" data-testid="video-tutorial-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Video Player */}
          <div className="aspect-video bg-slate-900 relative">
            {videoId ? (
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?rel=0`}
                title={title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mb-4">
                  <Play className="h-10 w-10 ml-1" />
                </div>
                <p className="text-lg font-semibold">Video Tutorial</p>
                <p className="text-slate-400 text-sm">Coming soon</p>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="p-6">
            <p className="text-slate-600 mb-6">{description}</p>

            {/* Step by Step Guide */}
            {steps && steps.length > 0 && (
              <div>
                <h3 className="font-bold text-slate-900 mb-4">Step-by-Step Guide</h3>
                <div className="space-y-4">
                  {steps.map((step, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="w-8 h-8 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">{step.title}</h4>
                        <p className="text-sm text-slate-600 mt-1">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end">
          <Button onClick={onClose} className="bg-orange-500 hover:bg-orange-600">
            Got it, thanks!
          </Button>
        </div>
      </div>
    </div>
  );
}

// Help Button Component - shows available tutorials
export function HelpButton({ tutorials }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTutorial, setActiveTutorial] = useState(null);

  return (
    <>
      {/* Help FAB */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-24 right-6 w-12 h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-full shadow-lg flex items-center justify-center z-40 transition-transform hover:scale-105"
        data-testid="help-button"
      >
        <span className="text-lg font-bold">?</span>
      </button>

      {/* Help Menu */}
      {isOpen && (
        <div className="fixed bottom-40 right-6 w-72 bg-white rounded-xl shadow-2xl border border-slate-200 z-40 animate-fade-in" data-testid="help-menu">
          <div className="p-4 border-b border-slate-100">
            <h3 className="font-bold text-slate-900">Video Tutorials</h3>
            <p className="text-sm text-slate-500">Learn how to use the app</p>
          </div>
          <div className="p-2">
            {tutorials.map((tutorial, index) => (
              <button
                key={index}
                onClick={() => {
                  setActiveTutorial(tutorial);
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 text-left transition-colors"
                data-testid={`tutorial-${index}`}
              >
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Play className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <div className="font-medium text-slate-900 text-sm">{tutorial.title}</div>
                  <div className="text-xs text-slate-500">{tutorial.duration}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Active Tutorial Modal */}
      {activeTutorial && (
        <VideoTutorialModal
          isOpen={true}
          onClose={() => setActiveTutorial(null)}
          title={activeTutorial.title}
          videoId={activeTutorial.videoId}
          description={activeTutorial.description}
          steps={activeTutorial.steps}
        />
      )}
    </>
  );
}

// Tutorial data for each role
export const customerTutorials = [
  {
    title: "How to Post a Moving Job",
    duration: "2 min",
    videoId: null, // Replace with actual YouTube video ID
    description: "Learn how to create a detailed moving job listing that attracts the best carriers.",
    steps: [
      { title: "Click 'Post New Job'", description: "Find the orange button in your dashboard header." },
      { title: "Choose Job Type", description: "Select household move, freight, or cargo based on your needs." },
      { title: "Enter Locations", description: "Add complete pickup and delivery addresses with zip codes." },
      { title: "Add Details", description: "Include weight estimate, pickup date, and any special instructions." },
      { title: "Submit & Wait", description: "Carriers will review and send quotes directly to you." }
    ]
  },
  {
    title: "How to Review & Accept Quotes",
    duration: "1 min",
    videoId: null,
    description: "Compare quotes from multiple carriers and choose the best option for your move.",
    steps: [
      { title: "View Your Jobs", description: "Go to your dashboard to see all posted jobs." },
      { title: "Click 'View Quotes'", description: "See all quotes received for each job." },
      { title: "Compare Options", description: "Review price, estimated delivery, and carrier details." },
      { title: "Chat with Carriers", description: "Ask questions before making a decision." },
      { title: "Accept the Best", description: "Click 'Accept' to confirm your choice." }
    ]
  }
];

export const carrierTutorials = [
  {
    title: "How to Submit Quotes",
    duration: "2 min",
    videoId: null,
    description: "Learn how to browse available jobs and submit competitive quotes to win business.",
    steps: [
      { title: "Browse Available Jobs", description: "View jobs in your dashboard or the Jobs page." },
      { title: "Review Job Details", description: "Check locations, dates, and requirements carefully." },
      { title: "Click 'Send Quote'", description: "Open the quote submission form." },
      { title: "Enter Your Price", description: "Provide a competitive rate based on the job scope." },
      { title: "Add Delivery Estimate", description: "Set a realistic delivery date." },
      { title: "Submit & Follow Up", description: "Chat with the customer to increase your chances." }
    ]
  },
  {
    title: "How to Post Driver Jobs",
    duration: "2 min",
    videoId: null,
    description: "Create job listings to attract qualified CDL and non-CDL drivers to your fleet.",
    steps: [
      { title: "Go to 'Driver Jobs'", description: "Navigate to the hiring section in your dashboard." },
      { title: "Click 'Post Driver Job'", description: "Open the job creation form." },
      { title: "Enter Job Details", description: "Add title, description, and requirements." },
      { title: "Specify License Type", description: "Select CDL-A, CDL-B, CDL-C, or Non-CDL." },
      { title: "Set Salary Range", description: "Provide competitive compensation details." },
      { title: "Publish & Review", description: "Post the job and review incoming applications." }
    ]
  }
];

export const driverTutorials = [
  {
    title: "How to Create Your Profile",
    duration: "2 min",
    videoId: null,
    description: "Build a professional driver profile that gets you noticed by top carriers.",
    steps: [
      { title: "Go to 'My Profile'", description: "Navigate to the Profile tab in your dashboard." },
      { title: "Click 'Create Profile'", description: "Start building your professional profile." },
      { title: "Select License Type", description: "Choose your CDL class or Non-CDL." },
      { title: "Enter License Details", description: "Add license number, state, and expiry date." },
      { title: "Add Experience", description: "Specify years of driving experience." },
      { title: "List Certifications", description: "Add Hazmat, Tanker, or other endorsements." },
      { title: "Write Your Bio", description: "Tell carriers why you're a great hire." }
    ]
  },
  {
    title: "How to Apply for Jobs",
    duration: "1 min",
    videoId: null,
    description: "Find and apply to driving positions that match your qualifications.",
    steps: [
      { title: "Browse Job Board", description: "Go to 'Find Jobs' in your dashboard." },
      { title: "Filter by License", description: "Show only jobs matching your license type." },
      { title: "Review Details", description: "Check salary, location, and requirements." },
      { title: "Click 'Apply Now'", description: "Start your application." },
      { title: "Add Cover Letter", description: "Optional but recommended to stand out." },
      { title: "Submit Application", description: "Carriers will contact you if interested." }
    ]
  }
];

// Dashboard tour steps for each role
export const customerTourSteps = [
  {
    target: '[data-testid="new-job-btn"]',
    title: "Post a Moving Job",
    description: "Click here to create a new job listing. Describe what needs to be moved and get quotes from carriers."
  },
  {
    target: '[data-testid="nav-jobs"]',
    title: "View Your Jobs",
    description: "See all your posted jobs, their status, and how many quotes you've received."
  },
  {
    target: '[data-testid="chat-widget-btn"]',
    title: "Chat with Carriers",
    description: "Use the chat feature to communicate directly with carriers about your move."
  }
];

export const carrierTourSteps = [
  {
    target: '[data-testid="nav-jobs"]',
    title: "Browse Available Jobs",
    description: "View jobs posted by customers and submit competitive quotes to win business."
  },
  {
    target: '[data-testid="nav-quotes"]',
    title: "Track Your Quotes",
    description: "See all quotes you've submitted and their acceptance status."
  },
  {
    target: '[data-testid="nav-drivers"]',
    title: "Browse Drivers",
    description: "Browse driver profiles and find qualified candidates for your fleet."
  },
  {
    target: '[data-testid="nav-hiring"]',
    title: "Post Driver Jobs",
    description: "Create job listings to attract CDL and non-CDL drivers to your fleet."
  },
  {
    target: '[data-testid="chat-widget-btn"]',
    title: "Chat with Customers",
    description: "Communicate directly with customers to discuss job details and win more business."
  }
];

export const driverTourSteps = [
  {
    target: '[data-testid="nav-profile"]',
    title: "Complete Your Profile",
    description: "Add your license info, experience, and certifications to get discovered by carriers."
  },
  {
    target: '[data-testid="nav-jobs"]',
    title: "Find Driving Jobs",
    description: "Browse positions from verified carriers that match your qualifications."
  },
  {
    target: '[data-testid="nav-applications"]',
    title: "Track Applications",
    description: "See the status of all your job applications in one place."
  }
];
